import { Check, ChevronDown, Search } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { usePopover } from "@/lib/usePopover";

/**
 * Выпадающий отбор с поиском.
 *
 * Заменяет нативный `<select>` не ради красоты: браузер рисует его список
 * СВОИМ стилем — белым на тёмной теме и без единого нашего токена, — и в
 * списке из ста сорока поставщиков нет поиска. Дальше выбора глазами по
 * алфавиту такой список не ведёт.
 *
 * Поле поиска стоит ВСЕГДА и получает фокус на открытии: «щёлкнул — начал
 * писать» это одно движение, тогда как «щёлкнул — нашёл глазами строку поиска
 * — щёлкнул в неё» три. Список из трёх пунктов от этого не страдает: там
 * поиск просто не нужен, и его никто не трогает.
 *
 * Значение — строка, как в адресе: этот компонент кладёт выбор прямо в
 * `?supplier=`, и число здесь стало бы лишним переводом туда-обратно.
 */

export type ComboOption = {
  value: string;
  label: string;
  /** Правая колонка строки: счётчик, объём — то, чем выбирают из равных. */
  meta?: string;
  /** Подсказка строки и материал для поиска: полный путь категории, к примеру. */
  title?: string;
  /** Кружок слева: цвет из CRM. «Молочный» и «Айвори» словами не различаются. */
  swatch?: string | null;
};

export function Combo({
  label,
  value,
  options,
  onChange,
  placeholder = "поиск",
  empty = "ничего не найдено",
  width = 210,
  trigger,
  align = "left",
  resetValue,
}: {
  label: string;
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  empty?: string;
  width?: number;
  /** Свой вид кнопки вместо поля с подписью — к примеру, подпись под
   *  счётчиком листания в шапке карточки. Попап тогда встаёт сразу под ним. */
  trigger?: (open: boolean, toggle: () => void) => ReactNode;
  /** К какому краю кнопки прижат попап: у правого края шапки левый край
   *  увёл бы список за экран. */
  align?: "left" | "right";
  /** Значение «без отбора» (`все поставщики`). Задано и выбрано другое —
   *  щелчок по подписи поля сбрасывает к нему, не открывая список: листать
   *  сто сорок строк вверх ради «все» — лишние движения (разметка 26.09.26
   *  07:24 п.2). */
  resetValue?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);
  const resettable = resetValue !== undefined && value !== resetValue;
  // НАЙДЕННОЕ РАНЖИРУЕТСЯ, а не только отбирается: точная подпись, начало
  // подписи, вхождение в подпись и лишь потом совпадение в подсказке; внутри
  // группы — исходный порядок. Enter берёт первую строку, и набранный код
  // `sheng` обязан выбрать `sheng`, а не «Itema», у которой «sheng» нашлось в
  // полном имени (поймано 15.09.26 на смене поставщика в шапке карточки).
  const shown = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    if (!needle) return options;
    const rank = (o: ComboOption) => {
      const label = o.label.toLowerCase();
      if (label === needle) return 0;
      if (label.startsWith(needle)) return 1;
      if (label.includes(needle)) return 2;
      return (o.title ?? "").toLowerCase().includes(needle) ? 3 : -1;
    };
    return options
      .map((o, i) => ({ o, i, r: rank(o) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.o);
  }, [options, draft]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    setCursor(
      Math.max(
        0,
        shown.findIndex((o) => o.value === value),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Клик мимо закрывает список; Esc ловит само поле (`keys`), а не документ.
  usePopover(open, () => setOpen(false), boxRef, { escape: false });

  // Курсор клавиатуры обязан оставаться видимым: в списке из ста сорока
  // строк «вниз» без прокрутки уводит выбор за край экрана.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.children[cursor] as HTMLElement | undefined;
    row?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
    setDraft("");
  };

  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setDraft("");
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setCursor((c) => Math.min(shown.length - 1, Math.max(0, c + step)));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const row = shown[cursor];
      if (row) pick(row.value);
    }
  };

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      {trigger ? (
        trigger(open, () => setOpen((v) => !v))
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={current?.title ?? label}
          className={
            "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
            (open
              ? "border-border-strong"
              : "border-border hover:border-border-strong")
          }
          style={{ maxWidth: width }}
        >
          <span
            className={
              "flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] " +
              (resettable ? "text-fg-muted hover:text-err hover:line-through" : "text-fg-faint")
            }
            title={resettable ? `сбросить: ${options.find((o) => o.value === resetValue)?.label ?? "без отбора"}` : undefined}
            onClick={
              resettable
                ? (e) => {
                    // Кнопка вокруг открыла бы список — сброс его не открывает.
                    e.stopPropagation();
                    setOpen(false);
                    onChange(resetValue);
                  }
                : undefined
            }
          >
            {label}
          </span>
          {current?.swatch ? <Swatch hex={current.swatch} /> : null}
          <span className="truncate px-2 text-fg">{current?.label ?? "—"}</span>
          <ChevronDown
            size={13}
            className="mr-1.5 ml-auto flex-none text-fg-faint"
          />
        </button>
      )}

      {open ? (
        <div
          className={
            "absolute z-30 min-w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg " +
            (trigger ? "top-full mt-1 " : "top-[34px] ") +
            (align === "right" ? "right-0" : "left-0")
          }
          style={{ width: Math.max(width, 240) }}
          // Попап: Enter на его кнопке нажимает кнопку, а не применяет тулбар «Товаров».
          data-popup=""
        >
          <div className="flex items-center gap-1.5 border-b border-border px-2">
            <Search size={12} className="flex-none text-fg-faint" />
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setCursor(0);
              }}
              onKeyDown={keys}
              placeholder={placeholder}
              className="h-8 w-full bg-transparent text-[13px] text-fg outline-none placeholder:text-fg-faint"
            />
          </div>
          <div ref={listRef} className="max-h-[280px] overflow-auto py-1">
            {shown.length === 0 ? (
              <div className="px-2.5 py-2 text-[12.5px] text-fg-faint">
                {empty}
              </div>
            ) : (
              shown.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  title={o.title}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(o.value)}
                  className={
                    "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] " +
                    (i === cursor ? "bg-bg-hover " : "") +
                    (o.value === value ? "text-accent-text" : "text-fg")
                  }
                >
                  {o.swatch !== undefined ? <Swatch hex={o.swatch} /> : null}
                  <span className="truncate">{o.label}</span>
                  {o.meta ? (
                    <span className="tnum ml-auto flex-none pl-2 text-[11.5px] text-fg-faint">
                      {o.meta}
                    </span>
                  ) : null}
                  {o.value === value ? (
                    <Check
                      size={12}
                      className={o.meta ? "flex-none" : "ml-auto flex-none"}
                    />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Кружок цвета. Пустой hex — пустой кружок, а не отсутствие места: строки
 *  списка иначе разъезжались бы влево-вправо. */
function Swatch({ hex }: { hex: string | null | undefined }) {
  return (
    <span
      className="size-3 flex-none rounded-full border border-border"
      style={{ background: hex || "transparent" }}
    />
  );
}
